import { occFetch } from "~/app/api/occ/occFetch";
import type { SessionCartIdentityData } from "~/app/store/bffApi";
import { cartRequestUrl, type CartRequestContext } from "~/features/cart/cart/api/CartRequestContext";

const CART_ALREADY_EXISTS_FOR_CONTEXT = "CART_ALREADY_EXISTS_FOR_CONTEXT";

export class CartCreationApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    super(`Cart creation failed with status ${status}`);
    this.name = "CartCreationApiError";
    this.status = status;
    this.body = body;
  }
}

export async function createCartForAdd(context: CartRequestContext): Promise<SessionCartIdentityData> {
  const response = await occFetch(cartRequestUrl(context), { method: "POST" }, { accountContext: "omit" });
  const body = (await response.json().catch(() => undefined)) as unknown;
  const identity = extractCreatedCartIdentity(body);
  if (!response.ok || !identity) {
    throw new CartCreationApiError(response.status, body);
  }
  return identity;
}

export function isCartAlreadyExistsForContext(error: unknown): error is CartCreationApiError {
  return (
    error instanceof CartCreationApiError &&
    error.status === 409 &&
    readOccErrorCode(error.body) === CART_ALREADY_EXISTS_FOR_CONTEXT
  );
}

function extractCreatedCartIdentity(body: unknown): SessionCartIdentityData | undefined {
  if (body === null || typeof body !== "object" || Array.isArray(body)) {
    return undefined;
  }
  const { code, guid } = body as { code?: unknown; guid?: unknown };
  if (typeof code !== "string" || !code || typeof guid !== "string" || !guid) {
    return undefined;
  }
  return { code, guid };
}

function readOccErrorCode(body: unknown): string | undefined {
  if (body === null || typeof body !== "object" || Array.isArray(body)) {
    return undefined;
  }
  const errors = (body as { errors?: unknown }).errors;
  if (!Array.isArray(errors) || errors.length !== 1) {
    return undefined;
  }
  const error = errors[0];
  if (error === null || typeof error !== "object" || Array.isArray(error)) {
    return undefined;
  }
  const errorCode = (error as { errorCode?: unknown }).errorCode;
  return typeof errorCode === "string" ? errorCode : undefined;
}
